import type { BundleArchiveBlob, BundleTreeFile } from '@hibro/shared';

import { type PickedDirectoryHandle, readPickedModule } from './linked-modules';

/**
 * Page-side pickers for local module install (见 12 / 18).
 *
 * These must run inside the click that started them. Routing "please pick" through
 * the SharedWorker and back would drop the user gesture, and
 * `showOpenFilePicker` / `showDirectoryPicker` would fail. The host only sees
 * the result afterwards, via `IBundleService.installLocalArchive` /
 * `installLocalDirectory`.
 */

/**
 * The picked archive's handle. Its own type rather than the `PickedFileHandle`
 * the linked-module walk uses: that one only ever reads small files whole, while
 * this hands the `File` on as a {@link BundleArchiveBlob} for the host to stream.
 */
interface PickedArchiveHandle {
  getFile(): Promise<BundleArchiveBlob>;
}

declare global {
  interface Window {
    showOpenFilePicker?(options?: {
      types?: Array<{
        description?: string;
        accept: Record<string, string[]>;
      }>;
      excludeAcceptAllOption?: boolean;
      multiple?: boolean;
    }): Promise<PickedArchiveHandle[]>;
  }
}

export type LocalBundlePick =
  | { kind: 'archive'; archive: BundleArchiveBlob }
  | { kind: 'dir'; files: BundleTreeFile[] };

/** `true` when this page can offer both local-install buttons. */
export function canPickLocalBundle(): boolean {
  return (
    typeof window.showOpenFilePicker === 'function' &&
    typeof window.showDirectoryPicker === 'function'
  );
}

/**
 * Open the native picker for `kind`. Cancelled picker → `null`.
 *
 * A directory is walked as the **delivered shape** (manifest + `assets` /
 * `nls` / `dist`), the same slice Link copies — a developer's working
 * directory also contains `node_modules/`, which must not enter the cache.
 */
export async function pickLocalBundle(
  kind: 'archive' | 'dir',
): Promise<LocalBundlePick | null> {
  try {
    if (kind === 'archive') {
      return await pickArchive();
    }
    return await pickDirectory();
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return null;
    }
    throw err;
  }
}

async function pickArchive(): Promise<LocalBundlePick> {
  // Bound rather than detached: these are `window` methods, and calling one
  // through a bare reference would invoke it with the wrong receiver.
  const pick = window.showOpenFilePicker?.bind(window);
  if (!pick) {
    throw new Error('this browser cannot pick a local file');
  }
  const [handle] = await pick({
    excludeAcceptAllOption: true,
    types: [
      {
        description: 'Module archive',
        accept: { 'application/gzip': ['.tgz', '.tar.gz'] },
      },
    ],
  });
  if (!handle) {
    throw new Error('no file was picked');
  }
  // The `File` itself crosses the channel, not its bytes: structured clone
  // carries a Blob by reference, so the host streams it straight off disk and
  // neither side ever holds the whole archive (见 12 本地资源侧载). No size
  // check here either — a local install is unbounded on both runtimes.
  return { kind: 'archive', archive: await handle.getFile() };
}

async function pickDirectory(): Promise<LocalBundlePick> {
  const pick = window.showDirectoryPicker?.bind(window);
  if (!pick) {
    throw new Error('this browser cannot pick a local directory');
  }
  const dir: PickedDirectoryHandle = await pick({ mode: 'read' });
  const { entries } = await readPickedModule(dir);
  return { kind: 'dir', files: entries };
}
